import { useTranslation } from 'react-i18next';
import React from 'react';
import { SortDirection } from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import { useLicenseManagementContext } from '../../../../contexts/LicenseManagementContext';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import {
    RESTRICTED_VIEWER_ROLE_NAME,
    translateUserRoles,
    VIEWER_ROLE_NAME,
    VISIBLE_ROLES,
} from '../roles';
import { useIdentityProviderContext } from '../../../../contexts/IdentityProviderContext';
import { getQsLocale } from '../../../../utilities/localization';
import { useI18nContext } from '../../../I18nProvider/I18nProvider';

export type TableViewSettings = {
    sort?: [SortColumn, SortDirection];
    filter: {
        name?: string;
        role?: (typeof VISIBLE_ROLES)[number];
        status?: boolean;
        idpId: undefined | string;
    };
    paging: [number, number];
};

type UserWithExtraContext = User & {
    rolesString: string;
    idpName: string;
    lastAccessString: string;
};

export const SORT_COLUMNS = [
    'userName',
    'rolesString',
    'idpName',
    'lastAccessString',
] as const;

type SortColumn = (typeof SORT_COLUMNS)[number];

export const useUserListTableData = (props: TableViewSettings) => {
    const { userSelectedLang } = useI18nContext();

    const {
        users,
        loaded: usersLoaded,
        error: usersError,
    } = useLicenseManagementContext();
    const {
        idps,
        loaded: idpsLoaded,
        error: idpsError,
    } = useIdentityProviderContext();

    const { sort, filter, paging } = props;

    const { t } = useTranslation();

    // add extra info into the user data
    const usersWithExtraContext: UserWithExtraContext[] = React.useMemo(
        () =>
            users?.map(u => ({
                ...u,
                rolesString: translateUserRoles(u.roles),
                idpName:
                    idps?.find(idp => idp.id === u.identityProviderId)
                        ?.displayName || '',
                lastAccessString: u.lastAccess
                    ? new Date(u.lastAccess).toLocaleString(
                          getQsLocale(userSelectedLang).qsLngCode,
                      )
                    : t('settings.licenseManagement.lastAccessNever'),
            })) || [],
        [t, users, idps, userSelectedLang],
    );

    // sort out user data
    const usersSorted = React.useMemo(() => {
        if (sort === undefined) {
            return usersWithExtraContext;
        }
        const sortCol = sort[0];
        const sortDir = sort[1];

        const sorted = usersWithExtraContext.sort((a, b) => {
            if (sortCol === 'lastAccessString') {
                return (
                    new Date(a.lastAccess || 1).getTime() -
                    new Date(b.lastAccess || 1).getTime()
                );
            }

            const aValue = a[sortCol] || '';
            const bValue = b[sortCol] || '';

            // Handle blank strings - put them at the end for ascending order
            if (aValue === '' && bValue !== '') return 1;
            if (bValue === '' && aValue !== '') return -1;
            if (aValue === '' && bValue === '') return 0;

            return aValue.localeCompare(bValue);
        });
        if (sortDir === 'desc') {
            sorted.reverse();
        }
        return sorted;
    }, [usersWithExtraContext, sort]);

    // filter out sorted user data
    const usersFiltered = React.useMemo(
        () =>
            usersSorted.filter(u => {
                const nameFilterOk = u.userName
                    .toLocaleLowerCase()
                    .includes(filter.name?.toLocaleLowerCase() || '');
                const roleFilterOk =
                    filter.role === undefined ||
                    (u.roles || [])
                        // TODO: remove restricted viewer role name mapping when role is removed from the system
                        .map(r =>
                            r.name === RESTRICTED_VIEWER_ROLE_NAME
                                ? VIEWER_ROLE_NAME
                                : r.name,
                        )
                        .includes(filter.role as any);

                const idpFilterOk =
                    filter.idpId === undefined ||
                    u.identityProviderId === filter.idpId;

                return nameFilterOk && roleFilterOk && idpFilterOk;
            }),
        [usersSorted, filter],
    );
    // limit filtered user data
    const usersLimited = React.useMemo(() => {
        const [page, perPage] = paging;

        return usersFiltered.slice((page - 1) * perPage, page * perPage);
    }, [usersFiltered, paging]);

    return {
        userPage: usersLimited,
        allUsersPostFilter: usersFiltered,
        allUsers: users,
        loaded: !usersError && !idpsError && usersLoaded && idpsLoaded,
        error: usersError || idpsError,
    };
};
